import type { PdfTemplateName } from '@/types/database';

interface PdfTemplatePreviewProps {
  template: PdfTemplateName;
  accent?: string;
  className?: string;
}

/**
 * Small schematic SVG preview of each PDF template.
 * ~100x140 aspect (matches A4 portrait).
 */
export function PdfTemplatePreview({
  template,
  accent = '#10B981',
  className,
}: PdfTemplatePreviewProps) {
  const w = 100;
  const h = 140;
  const gray = '#e5e7eb';
  const grayDark = '#9ca3af';
  const dark = '#1f2937';

  const wrap = (children: React.ReactNode) => (
    <svg
      className={className}
      viewBox={`0 0 ${w} ${h}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`Preview ${template}`}
    >
      <rect width={w} height={h} fill="white" stroke={gray} strokeWidth="1" />
      {children}
    </svg>
  );

  switch (template) {
    case 'classico':
      return wrap(
        <>
          <rect x="0" y="0" width={w} height="22" fill={accent} />
          <rect x="70" y="4" width="14" height="14" fill="white" opacity="0.85" />
          <rect x="4" y="6" width="40" height="4" fill="white" />
          <rect x="4" y="13" width="30" height="2.5" fill="white" opacity="0.8" />
          <rect x="4" y="17" width="24" height="2" fill="white" opacity="0.7" />
          {/* client box */}
          <rect x="4" y="28" width={w - 8} height="14" fill="#f9fafb" stroke={accent} strokeWidth="0.5" />
          <rect x="6" y="31" width="30" height="2" fill={grayDark} />
          <rect x="6" y="35" width="20" height="2" fill={dark} />
          {/* table */}
          <rect x="4" y="48" width={w - 8} height="4" fill={accent} />
          {[0, 1, 2, 3].map((i) => (
            <rect
              key={i}
              x="4"
              y={54 + i * 5}
              width={w - 8}
              height="4"
              fill={i % 2 === 0 ? '#f9fafb' : 'white'}
              stroke={gray}
              strokeWidth="0.3"
            />
          ))}
          {/* total */}
          <rect x="55" y="80" width="41" height="7" fill={accent} />
          {/* observations */}
          <rect x="4" y="92" width={w - 8} height="14" fill={gray} />
          {/* signatures */}
          <line x1="6" y1="122" x2="40" y2="122" stroke={grayDark} strokeWidth="0.5" />
          <line x1="60" y1="122" x2="94" y2="122" stroke={grayDark} strokeWidth="0.5" />
        </>,
      );

    case 'minimalista':
      return wrap(
        <>
          <rect x="6" y="8" width="45" height="4" fill={dark} />
          <rect x="80" y="6" width="14" height="14" fill={gray} />
          <rect x="6" y="16" width="30" height="2" fill={grayDark} />
          <rect x="6" y="20" width="40" height="2" fill={grayDark} />
          <line x1="6" y1="27" x2={w - 6} y2="27" stroke={accent} strokeWidth="1" />
          <rect x="6" y="32" width="10" height="2.5" fill={dark} />
          <rect x="20" y="32" width="30" height="2.5" fill={grayDark} />
          <rect x="6" y="38" width="10" height="2.5" fill={dark} />
          <rect x="20" y="38" width="40" height="2.5" fill={grayDark} />
          {/* table borderless */}
          <line x1="6" y1="52" x2={w - 6} y2="52" stroke={dark} strokeWidth="0.6" />
          {[0, 1, 2, 3, 4].map((i) => (
            <line
              key={i}
              x1="6"
              y1={58 + i * 5}
              x2={w - 6}
              y2={58 + i * 5}
              stroke={gray}
              strokeWidth="0.3"
            />
          ))}
          {/* total minimal */}
          <line x1="60" y1="90" x2={w - 6} y2="90" stroke={dark} strokeWidth="0.5" />
          <rect x="70" y="93" width="24" height="3" fill={dark} />
          {/* signatures */}
          <line x1="6" y1="122" x2="40" y2="122" stroke={grayDark} strokeWidth="0.5" />
          <line x1="60" y1="122" x2="94" y2="122" stroke={grayDark} strokeWidth="0.5" />
        </>,
      );

    case 'corporativo':
      return wrap(
        <>
          <rect x="0" y="0" width={w} height="24" fill="#1f2937" />
          <rect x="80" y="4" width="16" height="16" fill="white" opacity="0.9" />
          <rect x="4" y="6" width="45" height="4" fill="white" />
          <rect x="4" y="13" width="30" height="2.5" fill="white" opacity="0.8" />
          <rect x="4" y="17" width="24" height="2" fill="white" opacity="0.6" />
          <rect x="0" y="24" width={w} height="1.5" fill={accent} />
          {/* client */}
          <rect x="4" y="30" width={w - 8} height="12" fill="#f3f4f6" />
          {/* table */}
          <rect x="4" y="46" width={w - 8} height="4" fill="#1f2937" />
          {[0, 1, 2, 3].map((i) => (
            <rect
              key={i}
              x="4"
              y={52 + i * 5}
              width={w - 8}
              height="4"
              fill={i % 2 === 0 ? '#f3f4f6' : 'white'}
              stroke={gray}
              strokeWidth="0.3"
            />
          ))}
          {/* total dark */}
          <rect x="55" y="78" width="41" height="7" fill="#1f2937" />
          {/* obs */}
          <rect x="4" y="90" width="30" height="3" fill="#1f2937" />
          {/* footer bar */}
          <rect x="0" y={h - 10} width={w} height="4" fill="#1f2937" />
        </>,
      );

    case 'moderno':
      return wrap(
        <>
          {/* sidebar */}
          <rect x="0" y="0" width="18" height={h} fill={accent} />
          <rect x="3" y="6" width="12" height="12" fill="white" opacity="0.9" />
          <rect x="3" y="22" width="12" height="1.5" fill="white" />
          <rect x="3" y="26" width="12" height="1.5" fill="white" opacity="0.7" />
          <rect x="3" y="30" width="12" height="1.5" fill="white" opacity="0.7" />
          {/* content */}
          <rect x="22" y="8" width="45" height="5" fill={dark} />
          <line x1="22" y1="16" x2="42" y2="16" stroke={accent} strokeWidth="1.5" />
          {/* client card */}
          <rect x="22" y="24" width={w - 26} height="14" fill="#f5f6f8" rx="2" />
          {/* table headers underline */}
          <line x1="22" y1="46" x2={w - 4} y2="46" stroke={dark} strokeWidth="0.6" />
          {[0, 1, 2, 3, 4].map((i) => (
            <line
              key={i}
              x1="22"
              y1={52 + i * 5}
              x2={w - 4}
              y2={52 + i * 5}
              stroke={gray}
              strokeWidth="0.3"
            />
          ))}
          {/* total accent block */}
          <rect x="55" y="80" width="41" height="10" fill={accent} rx="2" />
          {/* signatures */}
          <line x1="22" y1="122" x2="50" y2="122" stroke={grayDark} strokeWidth="0.5" />
          <line x1="65" y1="122" x2="94" y2="122" stroke={grayDark} strokeWidth="0.5" />
        </>,
      );

    case 'elegante':
      return wrap(
        <>
          {/* outer frame */}
          <rect x="4" y="4" width={w - 8} height={h - 8} fill="none" stroke={accent} strokeWidth="0.7" />
          <rect x="6" y="6" width={w - 12} height={h - 12} fill="none" stroke={dark} strokeWidth="0.3" />
          {/* centered logo */}
          <rect x={w / 2 - 8} y="12" width="16" height="10" fill={gray} />
          {/* uppercase spaced name */}
          <rect x={w / 2 - 22} y="27" width="44" height="3" fill={dark} />
          <rect x={w / 2 - 15} y="34" width="30" height="1.5" fill={grayDark} />
          {/* accent rule */}
          <line x1={w / 2 - 10} y1="40" x2={w / 2 + 10} y2="40" stroke={accent} strokeWidth="0.6" />
          {/* title */}
          <rect x={w / 2 - 14} y="46" width="28" height="4" fill={dark} />
          {/* client rows */}
          <rect x="12" y="58" width="12" height="2" fill={grayDark} />
          <rect x="26" y="58" width="30" height="2" fill={dark} />
          <rect x="12" y="63" width="12" height="2" fill={grayDark} />
          <rect x="26" y="63" width="35" height="2" fill={dark} />
          {/* table thin lines only */}
          <line x1="12" y1="74" x2={w - 12} y2="74" stroke={dark} strokeWidth="0.3" />
          {[0, 1, 2, 3].map((i) => (
            <line
              key={i}
              x1="12"
              y1={80 + i * 5}
              x2={w - 12}
              y2={80 + i * 5}
              stroke={gray}
              strokeWidth="0.25"
            />
          ))}
          <line x1="60" y1="104" x2={w - 12} y2="104" stroke={dark} strokeWidth="0.4" />
          <rect x="68" y="107" width="24" height="3" fill={dark} />
          <line x1="60" y1="112" x2={w - 12} y2="112" stroke={dark} strokeWidth="0.4" />
          <line x1="14" y1="126" x2="42" y2="126" stroke={grayDark} strokeWidth="0.4" />
          <line x1="58" y1="126" x2="86" y2="126" stroke={grayDark} strokeWidth="0.4" />
        </>,
      );

    case 'botanico':
      return wrap(
        <>
          {/* pastel top band + sprig */}
          <rect x="0" y="0" width={w} height="6" fill={accent} opacity="0.15" />
          <path d="M78 16 q 8 -5 16 -5" stroke={accent} strokeWidth="0.8" fill="none" opacity="0.6" />
          {[0, 1, 2].map((i) => (
            <circle key={i} cx={82 + i * 5} cy={12.5 - i} r={2 - i * 0.4} fill={accent} opacity="0.35" />
          ))}
          {/* centered serif header */}
          <rect x={w / 2 - 8} y="12" width="16" height="10" fill={gray} />
          <rect x={w / 2 - 20} y="27" width="40" height="3" fill={dark} />
          <rect x={w / 2 - 14} y="33" width="28" height="1.5" fill={grayDark} />
          {/* divider with dot */}
          <line x1={w / 2 - 18} y1="39" x2={w / 2 - 3} y2="39" stroke={accent} strokeWidth="0.5" opacity="0.7" />
          <line x1={w / 2 + 3} y1="39" x2={w / 2 + 18} y2="39" stroke={accent} strokeWidth="0.5" opacity="0.7" />
          <circle cx={w / 2} cy="39" r="1" fill={accent} opacity="0.7" />
          {/* title */}
          <rect x={w / 2 - 16} y="44" width="32" height="4" fill={dark} />
          {/* client rows */}
          <rect x="8" y="54" width="12" height="2" fill={grayDark} />
          <rect x="24" y="54" width="28" height="2" fill={dark} />
          <rect x="8" y="59" width="12" height="2" fill={grayDark} />
          <rect x="24" y="59" width="34" height="2" fill={dark} />
          {/* table: pastel head, light lines */}
          <rect x="8" y="68" width={w - 16} height="4" fill={accent} opacity="0.25" />
          {[0, 1, 2, 3].map((i) => (
            <line
              key={i}
              x1="8"
              y1={77 + i * 5}
              x2={w - 8}
              y2={77 + i * 5}
              stroke={accent}
              strokeWidth="0.3"
              opacity="0.4"
            />
          ))}
          {/* total pastel box */}
          <rect x="56" y="100" width="36" height="7" fill={accent} opacity="0.25" rx="1.5" />
          {/* signatures */}
          <line x1="8" y1="122" x2="42" y2="122" stroke={grayDark} strokeWidth="0.5" />
          <line x1="58" y1="122" x2="92" y2="122" stroke={grayDark} strokeWidth="0.5" />
          {/* bottom band */}
          <rect x="0" y={h - 3} width={w} height="3" fill={accent} opacity="0.15" />
        </>,
      );

    case 'onda':
      return wrap(
        <>
          {/* waves */}
          <path d={`M0 0 H${w} V13 Q ${w * 0.7} 20 ${w * 0.5} 12 T 0 11 Z`} fill={accent} opacity="0.45" />
          <path d={`M0 0 H${w} V4 Q ${w * 0.65} 13 ${w * 0.4} 6 T 0 8 Z`} fill={accent} />
          {/* logo card over waves */}
          <rect x="6" y="3" width="12" height="12" fill="white" rx="1.5" />
          {/* big title + company right */}
          <rect x="6" y="22" width="34" height="5" fill={dark} />
          <rect x="64" y="21" width="30" height="2.5" fill={dark} />
          <rect x="74" y="25" width="20" height="1.5" fill={grayDark} />
          {/* form rows with underlines */}
          {[0, 1, 2, 3].map((i) => (
            <g key={i}>
              <rect x="6" y={33 + i * 6} width="12" height="2" fill={dark} />
              <rect x="22" y={33 + i * 6} width={30 + (i % 2) * 14} height="2" fill={grayDark} />
              <line x1="20" y1={36.5 + i * 6} x2={w - 6} y2={36.5 + i * 6} stroke={accent} strokeWidth="0.4" opacity="0.5" />
            </g>
          ))}
          {/* table grid in accent */}
          <rect x="6" y="62" width={w - 12} height="24" fill="none" stroke={accent} strokeWidth="0.6" />
          {[0, 1, 2, 3].map((i) => (
            <line key={i} x1="6" y1={66 + i * 5} x2={w - 6} y2={66 + i * 5} stroke={accent} strokeWidth="0.3" />
          ))}
          <line x1="50" y1="62" x2="50" y2="86" stroke={accent} strokeWidth="0.3" />
          <line x1="72" y1="62" x2="72" y2="86" stroke={accent} strokeWidth="0.3" />
          {/* total dark box */}
          <rect x="56" y="92" width="38" height="8" fill={dark} rx="1.5" />
          {/* signatures */}
          <line x1="6" y1="122" x2="40" y2="122" stroke={grayDark} strokeWidth="0.5" />
          <line x1="60" y1="122" x2="94" y2="122" stroke={grayDark} strokeWidth="0.5" />
        </>,
      );

    case 'tecnico':
      return wrap(
        <>
          {/* title left + accent underline, company right */}
          <rect x="6" y="8" width="34" height="5" fill="#4b5563" />
          <line x1="6" y1="16" x2="24" y2="16" stroke={accent} strokeWidth="1.2" />
          <rect x="64" y="7" width="30" height="2.5" fill="#4b5563" />
          <rect x="72" y="11" width="22" height="1.5" fill={grayDark} />
          <rect x="72" y="14.5" width="22" height="1.5" fill={grayDark} />
          <line x1="6" y1="22" x2={w - 6} y2="22" stroke={gray} strokeWidth="0.4" />
          {/* client gray rounded box */}
          <rect x="6" y="26" width={w - 12} height="16" fill="#f3f4f6" rx="2.5" />
          <rect x="9" y="30" width="26" height="2" fill={grayDark} />
          <rect x="9" y="35" width="34" height="2" fill={grayDark} />
          {/* table gray head + grid */}
          <rect x="6" y="48" width={w - 12} height="4" fill="#d9dbdf" />
          {[0, 1, 2, 3].map((i) => (
            <rect
              key={i}
              x="6"
              y={54 + i * 5}
              width={w - 12}
              height="4"
              fill="white"
              stroke={gray}
              strokeWidth="0.3"
            />
          ))}
          {/* obs box (left) + total dark (right) side by side */}
          <rect x="6" y="80" width="52" height="18" fill="#f3f4f6" rx="1.5" />
          <rect x="9" y="83" width="20" height="2" fill="#4b5563" />
          <rect x="9" y="88" width="44" height="1.5" fill={grayDark} />
          <rect x="9" y="91.5" width="38" height="1.5" fill={grayDark} />
          <rect x="62" y="80" width="32" height="8" fill="#4b5563" rx="1" />
          {/* signatures */}
          <line x1="6" y1="122" x2="40" y2="122" stroke={grayDark} strokeWidth="0.5" />
          <line x1="60" y1="122" x2="94" y2="122" stroke={grayDark} strokeWidth="0.5" />
        </>,
      );

    case 'compacto':
      return wrap(
        <>
          {/* thin colored header */}
          <rect x="0" y="0" width={w} height="8" fill={accent} />
          <rect x="4" y="2" width="20" height="4" fill="white" />
          <rect x={w / 2 - 12} y="2" width="24" height="4" fill="white" opacity="0.85" />
          <rect x={w - 24} y="2" width="20" height="4" fill="white" opacity="0.85" />
          {/* client single line */}
          <rect x="4" y="13" width="40" height="2" fill={dark} />
          <rect x="60" y="13" width="34" height="2" fill={grayDark} />
          <line x1="4" y1="19" x2={w - 4} y2="19" stroke={gray} strokeWidth="0.3" />
          {/* dense table */}
          <rect x="4" y="22" width={w - 8} height="3" fill={accent} />
          {Array.from({ length: 14 }, (_, i) => (
            <rect
              key={i}
              x="4"
              y={26 + i * 3.2}
              width={w - 8}
              height="3"
              fill={i % 2 === 0 ? '#f9fafb' : 'white'}
              stroke={gray}
              strokeWidth="0.2"
            />
          ))}
          {/* inline total */}
          <rect x={w - 40} y="76" width="14" height="2" fill={grayDark} />
          <rect x={w - 24} y="76" width="20" height="3" fill={accent} />
          {/* signatures */}
          <line x1="6" y1="122" x2="40" y2="122" stroke={grayDark} strokeWidth="0.5" />
          <line x1="60" y1="122" x2="94" y2="122" stroke={grayDark} strokeWidth="0.5" />
        </>,
      );

    case 'proposta':
      return wrap(
        <>
          {/* título + accent fino, como o minimalista */}
          <rect x="6" y="8" width="40" height="5" fill={dark} />
          <rect x="6" y="16" width="26" height="2" fill={grayDark} />
          <line x1="6" y1="24" x2={w - 6} y2="24" stroke={accent} strokeWidth="0.8" />
          {/* cliente em linhas simples */}
          <rect x="6" y="30" width="30" height="2" fill={grayDark} />
          <rect x="6" y="35" width="38" height="2" fill={grayDark} />
          {/* tabela de ESCOPO: descrição larga + 2 colunas estreitas, SEM valores */}
          <line x1="6" y1="48" x2={w - 6} y2="48" stroke={dark} strokeWidth="0.5" />
          {[0, 1, 2, 3, 4].map((i) => (
            <g key={i}>
              <rect x="6" y={52 + i * 6} width="56" height="2" fill={grayDark} />
              <rect x="68" y={52 + i * 6} width="8" height="2" fill={gray} />
              <rect x="80" y={52 + i * 6} width="8" height="2" fill={gray} />
              <line
                x1="6"
                y1={56 + i * 6}
                x2={w - 6}
                y2={56 + i * 6}
                stroke={gray}
                strokeWidth="0.2"
              />
            </g>
          ))}
          {/* o espaço do TOTAL fica deliberadamente VAZIO — é o que define o modelo */}
          {/* observações */}
          <rect x="6" y="92" width="20" height="2" fill={dark} />
          <rect x="6" y="97" width={w - 30} height="1.5" fill={grayDark} />
          <rect x="6" y="100.5" width={w - 40} height="1.5" fill={grayDark} />
          {/* signatures */}
          <line x1="6" y1="122" x2="40" y2="122" stroke={grayDark} strokeWidth="0.5" />
          <line x1="60" y1="122" x2="94" y2="122" stroke={grayDark} strokeWidth="0.5" />
        </>,
      );
  }
}

export const PDF_TEMPLATE_LABELS: Record<PdfTemplateName, string> = {
  classico: 'Clássico',
  minimalista: 'Minimalista',
  corporativo: 'Corporativo',
  moderno: 'Moderno',
  elegante: 'Elegante',
  compacto: 'Compacto',
  botanico: 'Botânico',
  onda: 'Onda',
  tecnico: 'Técnico',
  proposta: 'Proposta (sem valores)',
};

export const PDF_TEMPLATE_DESCRIPTIONS: Record<PdfTemplateName, string> = {
  classico: 'Cabeçalho colorido, tabela em grade. Padrão da Orçamigo.',
  minimalista: 'Sem cabeçalho colorido. Só preto/cinza + accent fino.',
  corporativo: 'Cabeçalho cinza escuro. Sério, ideal para engenharia/consultoria.',
  moderno: 'Barra lateral colorida à esquerda. Design contemporâneo.',
  elegante: 'Moldura dupla, logo centralizada, letra espaçada.',
  compacto: 'Fonte pequena, otimizado para muitos itens. Sem fotos.',
  botanico: 'Serifado, tons pastel, centrado. Ideal para estética/bem-estar.',
  onda: 'Ondas no cabeçalho, estilo formulário. Leve e amigável.',
  tecnico: 'Cinza neutro, caixas retas. Ideal para construção/reforma.',
  proposta: 'Só o escopo, sem preços. Para aprovar o serviço antes de falar de valor.',
};
